import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { DcColumnTypeModule } from '@datachain/ui-sdk/components/app-column-type';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { ColumnType } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { OptionChangedEvent } from 'devextreme/ui/accordion';
import { RowDraggingReorderEvent } from 'devextreme/ui/data_grid';
import { ValueChangedEvent } from 'devextreme/ui/select_box';
import { ValidationCallbackData } from 'devextreme/ui/validation_rules';
import { ValidationResult } from 'devextreme/ui/validator';
import { Map, OrderedMap } from 'immutable';
import {
	BehaviorSubject,
	combineLatest,
	firstValueFrom,
	map,
	Observable,
	tap,
} from 'rxjs';

import {
	AvailableNumberFormats,
	DcTimestampFormat,
	DefaultDateFormat,
	DefaultNumberFormat,
	DefaultTimezone,
} from '../../../core/dc-core-types';
import { FunctionalDocumentationKeys } from '../../../core/documentation/functional-doc-keys';
import { DxOperations } from '../../../core/dx-types';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import { DepotSchemaConfig } from '../../domain/depot-schema-config';
import {
	DefaultLanguage,
	SupportedLanguageEntity,
} from '../../domain/supported-language.entity';
import {
	DepotSelector,
	initDepotHeaderSetting,
	replaceCurrentLabelWith,
	triggerDetectAvailableHeaders,
} from '../../store';
import { DepotSchemaViewerComponent } from '../depot-schema-viewer/depot-schema-viewer.component';
import {
	DepotSchemaEntity,
	SchemaDataStructure,
} from '../depot-schema-viewer/depot-schema.entity';
import { HeaderMappingItemEntity } from './header-mapping-item.entity';
import { HeaderMappingListEntity } from './header-mapping-list.entity';
import { HeaderMappingListProvider } from './header-mapping-list.provider';

enum DepotHeaderStep {
	Selection = 0,
	Settings = 1,
}

enum ValidationStatus {
	Valid = 'valid',
	Invalid = 'invalid',
}

type HeaderOverlayDetails = {
	detailsHeaderId: number | string | undefined;
};

interface DepotMappingWithDetails {
	config: HeaderMappingItemEntity;
	labelStatus: ValidationStatus;
	labelError: string | null;
	canBeMapped: boolean;
}

@Component({
	selector: 'app-depot-header-mapping-modal',
	templateUrl: './header-mapping-modal.component.html',
	styleUrls: ['./header-mapping-modal.component.scss'],
	providers: [HeaderMappingListProvider],
})
export class AppHeaderMappingModalComponent<
		T extends { depotConfig: DepotSchemaConfig }
	>
	extends DcBaseComponent
	implements OnInit
{
	protected readonly DcIcons = DcIcons;
	protected readonly NO_ERRORS = new Array<''>();
	protected readonly DepotHeaderStep = DepotHeaderStep;
	protected readonly ColumnType = ColumnType;
	protected readonly DcLoaders = DcLoaders;
	protected readonly DataStruct = SchemaDataStructure;
	protected readonly FunctionalDocumentationKeys = FunctionalDocumentationKeys;
	protected readonly AvailableNumberFormats = AvailableNumberFormats;

	public isExpanded = true;
	public hasAllLeafsSelected = null;
	public structureSearchTerm = '';
	public structureReplaceWithTerm = '';
	public replaceWith = false;
	public autoRemoveDuplicates = 0;
	public resetLabels = 0;
	public replaceWithPath = 0;

	public structureValidity$ = new BehaviorSubject<Array<string>>(
		this.NO_ERRORS
	);
	public selectedHeaders$ = new BehaviorSubject<Array<DepotSchemaEntity>>([]);
	public isMiniViewerActive$ = new BehaviorSubject<boolean>(true);

	public filterValue: [string, DxOperations, string | null] | null = null;

	public validateDuplicate = async (
		e: ValidationCallbackData
	): Promise<boolean> => {
		if (!e.value) {
			return true;
		} // skip empty
		const depotMappingElements = await firstValueFrom(
			this.depotMappingElements$
		);
		const allLabelsArray = depotMappingElements
			.valueSeq()
			.map((item) => item.config.label)
			.toArray();
		const occurrences = allLabelsArray.filter((l) => l === e.value).length;
		return occurrences < 1;
	};

	public onReorder = async (e: RowDraggingReorderEvent): Promise<void> => {
		const visibleRows = e.component.getVisibleRows();
		const toId: number = visibleRows[e.toIndex].data.config.id;
		const fromId: number = e.itemData.config.id;

		const depotMappingElements = await firstValueFrom(
			this.depotMappingElements$
		);
		const headerList = depotMappingElements.toList();

		const fromIndex = headerList.findIndex(
			(value) => value.config.id === fromId
		);
		const toIndex = headerList.findIndex((value) => value.config.id === toId);

		const movedHeader = headerList.get(fromIndex);

		if (!movedHeader) {
			return;
		}
		let updatedList = headerList.delete(fromIndex);
		updatedList = updatedList.insert(toIndex, movedHeader);

		const updatedMap = OrderedMap<string, DepotMappingWithDetails>(
			updatedList.map((value, index) => [
				`${value.config.id}`,
				{
					...value,
					config: value.config.setPosition(index),
				},
			])
		);

		this.depotMappingElements$.next(updatedMap);
	};

	public getLabel = (row: DepotMappingWithDetails): string => row.config.label;
	public getDataType = (row: DepotMappingWithDetails): DcColumnTypeModule =>
		row.config.dataType;

	public vo$!: Observable<{
		headers: Array<DepotSchemaEntity> | null;
		selectedHeaders: Array<DepotSchemaEntity>;
		structureValidity: Array<string>;
		isMiniViewerActive: boolean;
		currentStep: number;
		settingErrors: number;
		depotMappingList: HeaderMappingListEntity;
		depotMappingElements: Map<string, DepotMappingWithDetails>;
		allowReordering: boolean;
		openedOverlays: HeaderOverlayDetails;
		panelActions: Array<PanelAction>;
		availableDateFormats: Map<string, DcTimestampFormat>;
		availableTimezones: Array<string>;
		supportedLanguages: Map<string, SupportedLanguageEntity>;
	}>;

	@ViewChild(DepotSchemaViewerComponent)
	private readonly depotHeaderMappingCmp!: DepotSchemaViewerComponent;

	private readonly currentStep$ = new BehaviorSubject<DepotHeaderStep>(
		DepotHeaderStep.Selection
	);

	private readonly depotMappingElements$ = new BehaviorSubject<
		OrderedMap<string, DepotMappingWithDetails>
	>(Map());

	private readonly allowReorderingForContentReady$ =
		new BehaviorSubject<boolean>(true);

	private readonly openedOverlaySubject$ =
		new BehaviorSubject<HeaderOverlayDetails>({
			detailsHeaderId: undefined,
		});

	private readonly panelActions$ = new BehaviorSubject<Array<PanelAction>>([]);

	private readonly filterErrorActive$ = new BehaviorSubject<boolean>(false);

	public constructor(
		private readonly depotSelector: DepotSelector,
		private readonly modalService: AppModalService,
		private readonly store: Store,
		private readonly headerMappingListProvider: HeaderMappingListProvider,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'depot-header-mapping';
	}

	public ngOnInit(): void {
		super.ngOnInit();

		this.vo$ = combineAll({
			headers: this.depotSelector.getDepotSchema$(),
			structureValidity: this.structureValidity$,
			selectedHeaders: this.selectedHeaders$,
			isMiniViewerActive: this.isMiniViewerActive$,
			currentStep: this.currentStep$,
			depotMappingList: this.headerMappingListProvider.getList(),
			depotMappingElements: this.depotMappingElements$,
			settingErrors: combineLatest([
				this.depotMappingElements$,
				this.filterErrorActive$,
			]).pipe(
				map(([elements, filterErrorActive]) => {
					const invalidValues = elements
						.toList()
						.toArray()
						.filter((item) => item.labelStatus === ValidationStatus.Invalid);

					const nbInvalidLines = invalidValues.length;
					return {
						nbInvalidLines,
						filterErrorActive,
					};
				}),
				tap(({ nbInvalidLines, filterErrorActive }) =>
					this.bootstrapIcons(nbInvalidLines, filterErrorActive)
				),
				map(({ nbInvalidLines }) => nbInvalidLines)
			),
			allowReordering: this.allowReorderingForContentReady$,
			openedOverlays: this.openedOverlaySubject$,
			panelActions: this.panelActions$,
			availableDateFormats: this.depotSelector.getAvailableDateFormats$(),
			availableTimezones: this.depotSelector.getAvailableTimezones$(),
			supportedLanguages: this.depotSelector.getSupportedLanguages$(),
		});
	}

	public triggerSchemaDetection(): void {
		this.store.dispatch(
			triggerDetectAvailableHeaders({
				params: this.modalData.depotConfig,
			})
		);
	}

	public triggerAutoRemoveDuplicates(): void {
		this.autoRemoveDuplicates = this.autoRemoveDuplicates + 1;
	}

	public triggerReplaceWithPath(): void {
		this.replaceWithPath = this.replaceWithPath + 1;
	}

	public triggerResetLabels(): void {
		this.resetLabels = this.resetLabels + 1;
	}

	public updateValidity(val: Array<string>): void {
		this.structureValidity$.next(val);
	}

	public retrieveSelectedHeaders($event: Array<DepotSchemaEntity>): void {
		this.selectedHeaders$.next($event);
	}

	public toggleSelectionViewer(): void {
		this.isMiniViewerActive$.next(!this.isMiniViewerActive$.value);
	}

	public triggerReplaceField($event: Event, toggle = true): void {
		if (toggle) {
			this.structureReplaceWithTerm = '';
			this.replaceWith = !this.replaceWith;
		}
		$event.preventDefault();
		$event.stopPropagation();
	}

	public triggerReplaceNameWith(
		$event: Event,
		current: string,
		replaceWith: string
	): void {
		this.store.dispatch(
			replaceCurrentLabelWith({
				searchTerm: current,
				replaceWith,
			})
		);
		$event.preventDefault();
		$event.stopPropagation();
	}

	public close(): void {
		this.modalService.closeActiveModal({
			depotMapping: null,
		});
	}

	public async save(): Promise<void> {
		const depotMappingElements = await firstValueFrom(
			this.depotMappingElements$
		);
		const { depotMapping } = await this.depotHeaderMappingCmp.extractData();
		const depotMappingConfigs = Map<string, HeaderMappingItemEntity>(
			Array.from(depotMappingElements.entries()).map(([key, value]) => [
				key,
				value.config,
			])
		);
		const result = this.depotHeaderMappingCmp.updateDepotSchema(
			depotMapping,
			depotMappingConfigs
		);

		this.modalService.closeActiveModal({
			depotMapping: result,
		});
	}

	public changeCurrentStep($event: StepperSelectionEvent): void {
		const currentStep = $event.selectedIndex as DepotHeaderStep;
		this.currentStep$.next(currentStep);
	}

	public nextStep(): void {
		this.currentStep$.next(DepotHeaderStep.Settings);
	}

	public backStep(): void {
		this.currentStep$.next(DepotHeaderStep.Selection);
	}

	public async onStepReady(): Promise<void> {
		const currentStep = await firstValueFrom(this.currentStep$);

		if (currentStep !== DepotHeaderStep.Settings) {
			return;
		}

		const availableCharacteristicTypes = await firstValueFrom(
			this.depotSelector.getAvailableCharacteristicTypes$()
		);

		const availableDateFormats = await firstValueFrom(
			this.depotSelector.getAvailableDateFormats$()
		);

		if (
			availableCharacteristicTypes.size === 0 ||
			availableDateFormats.size === 0
		) {
			this.store.dispatch(initDepotHeaderSetting());
		}

		const selectedHeaders = await firstValueFrom(this.selectedHeaders$);
		const depotMappingElements = await firstValueFrom(
			this.depotMappingElements$
		);

		// user flow (new schema detection) step1 -> step2 -> step1 -> step2
		// user flow (re-open schema detection) step 1
		if (depotMappingElements.size !== 0) {
			const elements = this.syncMappingWithSchema(
				selectedHeaders,
				depotMappingElements
			);

			this.depotMappingElements$.next(elements);
			return;
		}

		// new headers
		// == user flow in edit: already saved -> schema detection -> step1 -> step2
		// == user flow in create: new -> schema detection -> step1 -> step2
		const unOrderedElements = selectedHeaders.filter(
			(entity) => entity.position === -1
		);

		if (unOrderedElements.length === selectedHeaders.length) {
			const depotMappingElements = selectedHeaders.reduce(
				(acc, curr, index) => {
					const entity = this.generateDepotMappingWithDetails(curr, index);
					acc.push([`${curr.id}`, entity]);
					return acc;
				},
				new Array<[string, DepotMappingWithDetails]>()
			);
			this.depotMappingElements$.next(OrderedMap(depotMappingElements));
			return;
		}

		// == user flow in edit: already saved -> schema detection -> step1 -> step2
		const orderedElements = selectedHeaders.filter(
			(entity) => entity.position !== -1
		);

		const orderedDepotMappingElements = Map(
			orderedElements.map((entity) => [
				`${entity.id}`,
				this.generateDepotMappingWithDetails(entity, entity.position),
			])
		);

		const elements = this.syncMappingWithSchema(
			selectedHeaders,
			orderedDepotMappingElements
		);

		this.depotMappingElements$.next(elements);
	}

	public async selectConvertTo(
		key: string,
		event: ValueChangedEvent
	): Promise<void> {
		const selectedValue = event.value as ColumnType;

		if (!selectedValue) {
			return;
		}

		const depotMappingElements = await firstValueFrom(
			this.depotMappingElements$
		);
		const elem = depotMappingElements.get(key);

		if (!elem) {
			return;
		}

		let updatedConfig = elem.config.setConvertTo(selectedValue);

		if (
			(elem.config.dataType === ColumnType.Date &&
				selectedValue === ColumnType.Str) ||
			(elem.config.dataType === ColumnType.Str &&
				selectedValue === ColumnType.Date)
		) {
			updatedConfig = updatedConfig
				.setFormat(DefaultDateFormat)
				.setTimezone(DefaultTimezone)
				.setLanguage(DefaultLanguage);
		} else if (
			(elem.config.dataType === ColumnType.Str &&
				selectedValue === ColumnType.Dec) ||
			(elem.config.dataType === ColumnType.Dec &&
				selectedValue === ColumnType.Str)
		) {
			updatedConfig = updatedConfig.setFormat(DefaultNumberFormat);
		} else {
			updatedConfig = updatedConfig.setFormat(null);
		}

		const updatedElement: DepotMappingWithDetails = {
			...elem,
			config: updatedConfig,
		};

		const updatedElements = depotMappingElements.set(key, updatedElement);
		this.depotMappingElements$.next(updatedElements);
	}

	public async selectFormat(
		key: string,
		event: ValueChangedEvent
	): Promise<void> {
		const selectedValue = event.value as ColumnType;

		if (!selectedValue) {
			return;
		}
		const depotMappingElements = await firstValueFrom(
			this.depotMappingElements$
		);

		const elem = depotMappingElements.get(key);

		if (!elem) {
			return;
		}
		const updatedConfig = elem.config.setFormat(selectedValue);

		const updatedElement: DepotMappingWithDetails = {
			...elem,
			config: updatedConfig,
		};

		const updatedElements = depotMappingElements.set(key, updatedElement);
		this.depotMappingElements$.next(updatedElements);
	}

	public async selectTimezone(
		key: string,
		event: ValueChangedEvent
	): Promise<void> {
		const selectedValue = event.value as string;

		if (!selectedValue) {
			return;
		}
		const depotMappingElements = await firstValueFrom(
			this.depotMappingElements$
		);

		const elem = depotMappingElements.get(key);

		if (!elem) {
			return;
		}
		const updatedConfig = elem.config.setTimezone(selectedValue);

		const updatedElement: DepotMappingWithDetails = {
			...elem,
			config: updatedConfig,
		};

		const updatedElements = depotMappingElements.set(key, updatedElement);
		this.depotMappingElements$.next(updatedElements);
	}

	public async selectLanguage(
		key: string,
		event: ValueChangedEvent
	): Promise<void> {
		const selectedValue = event.value as string;

		if (!selectedValue) {
			return;
		}
		const depotMappingElements = await firstValueFrom(
			this.depotMappingElements$
		);

		const elem = depotMappingElements.get(key);

		if (!elem) {
			return;
		}
		const updatedConfig = elem.config.setLanguage(selectedValue);

		const updatedElement: DepotMappingWithDetails = {
			...elem,
			config: updatedConfig,
		};

		const updatedElements = depotMappingElements.set(key, updatedElement);
		this.depotMappingElements$.next(updatedElements);
	}

	public async updateHeaderLabel(
		key: string,
		event: ValidationResult
	): Promise<void> {
		const depotMappingElements = await firstValueFrom(
			this.depotMappingElements$
		);
		const elem = depotMappingElements.get(key);

		if (!elem) {
			return;
		}

		const { brokenRule, value } = event;

		const updatedConfig = elem.config.setLabel(value);

		const updatedElement: DepotMappingWithDetails = {
			...elem,
			config: updatedConfig,
			labelStatus:
				event.status === 'valid'
					? ValidationStatus.Valid
					: ValidationStatus.Invalid,
			labelError: brokenRule?.message ?? null,
		};

		const updatedElements = depotMappingElements.set(key, updatedElement);
		this.depotMappingElements$.next(updatedElements);
	}

	public onGridOptionChanged(event: OptionChangedEvent): void {
		// Check if sorting or filtering is applied
		const action: string = event.fullName;
		const { value } = event;
		if (action.includes('sortOrder') || action.includes('filterValue')) {
			this.allowReorderingForContentReady$.next(!value);
		}
	}

	public async openHeaderDetailsPopover(
		event: Event,
		headerId: string
	): Promise<void> {
		event.stopPropagation();

		const previous = await firstValueFrom(this.openedOverlaySubject$);
		if (previous.detailsHeaderId === headerId) {
			this.openedOverlaySubject$.next({
				...previous,
				detailsHeaderId: undefined,
			});
			return;
		}

		this.openedOverlaySubject$.next({
			...previous,
			detailsHeaderId: headerId,
		});
	}

	public closeOverlay(): void {
		this.openedOverlaySubject$.next({
			detailsHeaderId: undefined,
		});
	}

	private async bootstrapIcons(
		invalidLines: number,
		filterErrorActive: boolean
	): Promise<void> {
		const actions = [ActionIcons.FilterWarning, ActionIcons.FilterOff];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);

		if (invalidLines === 0) {
			this.panelActions$.next([]);
			this.filterValue = null;
			if (filterErrorActive) {
				this.filterErrorActive$.next(false);
			}
			return;
		}

		if (filterErrorActive) {
			this.panelActions$.next([
				{
					order: 0,
					command: (): void => {
						this.filterValue = null;
						this.filterErrorActive$.next(false);
					},
					iconName: actionMap.get(ActionIcons.FilterOff)?.iconName,
					description: '',
					tooltip: $localize`:i18n=@@depot.header.mapping.list.action.filterOff:`,
					typed: '',
				},
			]);
			return;
		}

		this.panelActions$.next([
			{
				order: 0,
				command: (): void => {
					this.filterValue = ['labelError', DxOperations.NotEqual, null];
					this.filterErrorActive$.next(true);
				},
				iconName: actionMap.get(ActionIcons.FilterWarning)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@depot.header.mapping.list.action.filterErrors:`,
				typed: '',
			},
		]);
	}

	private syncMappingWithSchema(
		selectedHeaders: Array<DepotSchemaEntity>,
		previousDepotMappingElements: OrderedMap<string, DepotMappingWithDetails>
	): OrderedMap<string, DepotMappingWithDetails> {
		const selectedIds = new Set(selectedHeaders.map((h) => h.id));

		const retainedExistingMappings = previousDepotMappingElements
			.toList()
			.toArray()
			.filter((item) => selectedIds.has(item.config.id))
			.sort((a, b) => a.config.position - b.config.position);

		const selectedHeadersMap = Map(selectedHeaders.map((h) => [h.id, h]));

		// Update positions for retained existing & label (if we have an update label in step 1)
		const updatedMappings: Array<DepotMappingWithDetails> =
			retainedExistingMappings.map((item, index) => {
				const selectedSchemaEntry = selectedHeadersMap.get(
					item.config.id
				) as DepotSchemaEntity;

				let headerMappingItem = item.config.setPosition(index++);

				const { previousLabel } = item.config;

				if (selectedSchemaEntry.currentLabel !== previousLabel) {
					headerMappingItem = headerMappingItem
						.setPreviousLabel(selectedSchemaEntry.currentLabel)
						.setLabel(selectedSchemaEntry.currentLabel);
				}

				if (selectedSchemaEntry.schemaElemType === SchemaDataStructure.Array) {
					headerMappingItem = this.handleSelectedArrayTypes(
						selectedSchemaEntry,
						headerMappingItem
					);
				}

				if (selectedSchemaEntry.schemaElemType === SchemaDataStructure.Struct) {
					headerMappingItem = headerMappingItem.setIsList(false); // FIXME: peut non necessaire
				}

				const canBeMapped =
					selectedSchemaEntry.schemaElemType === SchemaDataStructure.Leaf &&
					!selectedSchemaEntry.shouldIgnoreSubtree;
				const displayedType =
					selectedSchemaEntry.schemaElemType === SchemaDataStructure.Leaf &&
					!selectedSchemaEntry.shouldIgnoreSubtree
						? (selectedSchemaEntry.dataType as ColumnType)
						: ColumnType.Str;
				headerMappingItem = headerMappingItem
					.setTransientDataType(displayedType)
					.setConvertTo(displayedType);

				return {
					...item,
					canBeMapped,
					config: headerMappingItem,
				};
			});

		// Add new elements that are not in existingElements
		const newItems: Array<DepotMappingWithDetails> = selectedHeaders
			.filter((header) => !previousDepotMappingElements.has(`${header.id}`))
			.map((header, index) =>
				this.generateDepotMappingWithDetails(
					header,
					updatedMappings.length + index
				)
			);

		const depotMappingElements = [...updatedMappings, ...newItems];

		return OrderedMap<string, DepotMappingWithDetails>(
			depotMappingElements.map((elem) => [`${elem.config.id}`, elem])
		);
	}

	private generateDepotMappingWithDetails(
		schemaEntry: DepotSchemaEntity,
		position: number
	): DepotMappingWithDetails {
		let headerMapping = HeaderMappingItemEntity.build({
			id: schemaEntry.id ?? '-1',
			previousLabel: schemaEntry.currentLabel,
			dataType:
				schemaEntry.schemaElemType === SchemaDataStructure.Leaf
					? schemaEntry.dataType ?? ColumnType.Unknown
					: ColumnType.Unknown,
			convertTo: schemaEntry.convertTo,
			format: schemaEntry.format,
			timezone: schemaEntry.timezone,
			language: schemaEntry.language,
			isList: schemaEntry.schemaElemType === SchemaDataStructure.Array,
			position,
		});

		if (schemaEntry.schemaElemType === SchemaDataStructure.Array) {
			headerMapping = this.handleSelectedArrayTypes(schemaEntry, headerMapping);
		}

		const canBeMapped =
			schemaEntry.schemaElemType === SchemaDataStructure.Leaf &&
			!schemaEntry.shouldIgnoreSubtree;
		const displayedType =
			schemaEntry.schemaElemType === SchemaDataStructure.Leaf &&
			!schemaEntry.shouldIgnoreSubtree
				? (schemaEntry.dataType as ColumnType)
				: ColumnType.Str;
		headerMapping = headerMapping
			.setTransientDataType(displayedType)
			.setConvertTo(displayedType);

		return {
			config: headerMapping,
			canBeMapped,
			labelStatus: ValidationStatus.Valid,
			labelError: null,
		};
	}

	private handleSelectedArrayTypes(
		selectedEntry: DepotSchemaEntity,
		currentMapping: HeaderMappingItemEntity
	): HeaderMappingItemEntity {
		if (selectedEntry.children[0].schemaElemType === 'LEAF') {
			const type = selectedEntry.isSerialized
				? ColumnType.Str
				: (selectedEntry.children[0].dataType as ColumnType);
			return currentMapping
				.setTransientDataType(type)
				.setConvertTo(type)
				.setIsList(!selectedEntry.isSerialized);
		}
		// Array of Struct/Array
		return currentMapping.setIsList(!selectedEntry.shouldIgnoreSubtree);
	}
}
